import React, { useState, useEffect } from "react";
import { Send } from "lucide-react";
import {
  ChatHeader,
  ChatSidebar,
  Message,
} from "@/components/mentoratComp/chatComponents";
import { initialConversations } from "../../data/initialData";
import type {
  Message as MessageType,
  Conversation,
  MenuAction,
} from "../../types/index";
import { useMentorApi } from "@/api/MentorApi";
import { TokenHelper } from "@/utils/tokensHelper";
import { jwtDecode } from "jwt-decode";

interface CustomJwtPayload {
  user_id: string;
}

const ChatApp = () => {
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [conversations, setConversations] =
    useState<Conversation[]>(initialConversations);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [messageSearch, setMessageSearch] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [selectedConversationId, setSelectedConversationId] = useState<
    number | null
  >(null);
  const { sendMessage, getMessages } = useMentorApi();

  const getCurrentUserId = (): string => {
    const tokenHelper = new TokenHelper();
    const { access } = tokenHelper.getTokens();

    if (!access) throw new Error("No access token available");

    const decoded = jwtDecode<CustomJwtPayload>(access);

    return decoded.user_id;
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const filteredConversations = conversations.filter(
    (conversation) =>
      conversation.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conversation.message?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredMessages = messages.filter((message) =>
    message.content.toLowerCase().includes(messageSearch.toLowerCase())
  );

  const currentUser =
    conversations.find((c) => c.id === selectedConversationId) ||
    conversations.find((c) => c.active);

  const handleConversationSelect = (id: number) => {
    setSelectedConversationId(id);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;

    const fetchAndPollMessages = async () => {
      if (!selectedConversationId) return;

      const currentUserId = getCurrentUserId();

      try {
        const fetchedMessages = await getMessages(
          selectedConversationId.toString()
        );

        if (Array.isArray(fetchedMessages)) {
          const formattedMessages: MessageType[] = fetchedMessages.map(
            (msg) => ({
              id: msg.id,
              sender: msg.sender_id.toString(),
              content: msg.message,
              time: new Date(msg.created_at).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              }),
              isCurrentUser: msg.sender_id === currentUserId,
            })
          );

          setMessages(formattedMessages);
        }
      } catch (err) {
        console.error("Erreur lors du rafraîchissement des messages", err);
      }
    };

    fetchAndPollMessages();

    interval = setInterval(fetchAndPollMessages, 5000);

    return () => clearInterval(interval);
  }, [selectedConversationId]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentUser) return;

    try {
      await sendMessage(currentUser.id.toString(), newMessage);
      const currentUserId = getCurrentUserId();

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          sender: currentUserId,
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
          content: newMessage,
          isCurrentUser: true,
        },
      ]);

      setNewMessage("");
    } catch (err) {
      console.error("Erreur envoi message", err);
    }
  };

  const handleMenuAction = (action: MenuAction) => {
    if (action.type === "pin" && currentUser) {
      setConversations((prevConversations) =>
        prevConversations.map((conv) =>
          conv.id === currentUser.id
            ? { ...conv, isPinned: !conv.isPinned }
            : conv
        )
      );
    }
  };

  return (
    <div className="min-h-screen bg-white pt-4">
      <div className="flex h-[calc(100vh-64px)]">
        <ChatSidebar
          isOpen={sidebarOpen}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          conversations={filteredConversations}
          onConversationSelect={handleConversationSelect}
          isMobile={isMobile}
        />

        <div className="flex-1 flex flex-col relative">
          {currentUser && (
            <ChatHeader
              avatar={currentUser.avatar || ""}
              name={currentUser.name}
              role={currentUser.role || ""}
              messageSearch={messageSearch}
              onMessageSearchChange={setMessageSearch}
              onBackClick={isMobile ? () => setSidebarOpen(true) : undefined}
              isMobile={isMobile}
              onMenuAction={handleMenuAction}
              isPinned={currentUser.isPinned}
            />
          )}

          <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-4">
            {filteredMessages.map((message) => (
              <Message key={message.id} {...message} />
            ))}
          </div>
          {(!isMobile || (isMobile && !sidebarOpen)) && (
            <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 md:p-4">
              <form
                onSubmit={handleSendMessage}
                className="flex items-center space-x-2"
              >
                <div className="flex-1 bg-gray-100 rounded-lg">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Écrivez votre message..."
                    className="w-full px-4 py-2 bg-transparent border-0 focus:ring-0 text-gray-900 placeholder-gray-500 text-sm md:text-base"
                  />
                </div>
                <button
                  type="submit"
                  className="bg-blue-500 text-white rounded-full p-2 hover:bg-blue-600 transition-colors"
                >
                  <Send className="w-5 h-5" />
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatApp;
